import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Website} from "../website/website";
import {DialogService} from "../../../ng2-bootstrap-modal/dialog.service";
import {IDropdownListItem} from "../../shared/components/dropdown-list/dropdown-list-item";
import {
  UserSubscription,
  UserSubscriptionIds,
  UserSubscriptionType
} from "../../user/user-subscription";
import {WebsiteService} from "../website.service";
import * as fromStore from "../../store";
import {DbStoreState} from "../../store";
import {Store} from '@ngrx/store';
import {User} from "../../user/user";
import {Config} from "../../config/config";
import {TagsListDialogComponent} from "../tag-managment-dialogs/tags-list-dialog/tags-list-dialog.component";
import {WebsiteTag} from "../website/website-tag";
import {Observable} from "rxjs";
import {ActivityStatus, IActivity} from "../../activities/activity";
import {Router} from "@angular/router";
import {GoogleAnalyticsService} from "../../shared/services/google-analytics/google-analytics.service";
import {distinctUntilChanged, filter, first, map, switchMap, takeUntil} from "rxjs/operators";
import {combineLatest} from "rxjs";
import {clientRoles} from "../../user/user-roles-actions";
import {Workspace} from "../../user-managment/workspace";
import {Subject} from "rxjs";
import {WebsitesListItem} from "../../shared/components/websites-list/websites-list-item.model";
import {TypeUtils} from "../../shared/utils/type-utils";
import {CreateWebsiteService} from "../../services/local/create-website.service";
import {CookieService} from "ngx-cookie";


@Component({
    selector: 'db-website-item',
    templateUrl: './website-item.component.html',
    styleUrls: ['./website-item.component.scss'],
    standalone: false
})
export class WebsiteItemComponent implements OnInit, OnDestroy {

  @Input() website!: Website;
  @Input() hostedWebsiteLimit: number = 0;
  @Input() hostedWebsitesCount: number = 0;
  @Input() hostedWebsitesIncludedMultiCount: number = 0;
  @Input() user: User;
  @Input() userSubscription: UserSubscription;
  @Input() allTagsList: WebsiteTag[];
  @Input() parentLoading?: boolean;
  @Input() disableMigrateButton?: boolean;
  @Input() isUploading?: boolean;
  @Input() demoUser?: boolean;
  @Output() refresh: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggled: EventEmitter<void> = new EventEmitter<void>();
  @Output() openDemoIframe: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('subSitesList', {read: ElementRef}) subSitesList: ElementRef;

  public loading: boolean;
  public ongoingGoogleScoreActivity$: Observable<IActivity>;
  public ongoingActivity$: Observable<IActivity[]>;
  public websiteActions: IDropdownListItem[];
  public disableCopyWebsite: boolean = false;
  public ongoingOptimizingActivities: boolean = false;

  public desktopScore: number;
  public mobileScore: number;
  public migrationInProgress: boolean;
  public ongoingMigrationActivity: any;
  public failedMigration: boolean = false;
  public currentWorkspace: Workspace;
  public clientRoles = clientRoles;
  public canMigrate: boolean;
  /** dbTesting indicates sign-in from backdoor */
  public displayWebsiteUrl: string;
  public overlay: boolean = false;
  public tagsList: WebsiteTag[];
  public tagsCount: number;
  public pluginUpdating = false;
  public cdnIsEnabled: boolean;
  public previewUrl: string;

  public networkSubSitesOptions$: Observable<WebsitesListItem[]>;

  public readonly userSubscriptionIds = UserSubscriptionIds;
  public readonly userSubscriptionType = UserSubscriptionType;
  public readonly defaultStarterPlanId = Config.defaultStarterPlanId;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _dialogService: DialogService,
    private _store: Store<DbStoreState>,
    private _websiteService: WebsiteService,
    private _router: Router,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _createWebsiteService: CreateWebsiteService,
    private _cookieService: CookieService
  ) {
  }

  ngOnInit() {
    this._initData();
    this._addSubscriptions();

    if (this.website.domain_hash && this.website.loggedOut) {
      this.websiteAutoLogin();
    }
    this._getOngoingOptimizingActivities();
    this._getOngoingManagerUpdateActivities();
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _initData(): void {
    this.displayWebsiteUrl = this.website.site_url.replace(/^https?:\/\//i, '');
    this._setWebsiteUrl();
    this.desktopScore =
      this.website.score && this.website.score.currentScore && this.website.score.currentScore.desktopScore;
    this.mobileScore = this.website.score && this.website.score.currentScore && this.website.score.currentScore.mobileScore;
    this.canMigrate =
      !this.website.isNetwork && this.user && !this.user.oneProductOrBundleClient;
    this.overlay = !this.website?.isActive || (!this.website?.domain_hash && this.website?.isLocalhost)
      || (!this.website?.domain_hash && this.website?.connectedFrom !== 'speed_optimizer'
        && this.user && (this.user.starterClient || this.user.platformClient)) || (!this.website?.domain_hash && this.website?.connectedFrom === 'speed_optimizer' && this.userSubscription && this.userSubscription.trialClient) || (this.website?.isAiBuilderDemo && this.user?.platformClient);
    this.tagsCount = window.innerWidth <= 1366 ? 2 : 3;
    this.tagsList =
      this.website.tagsDefaultList ?
        <WebsiteTag[]>Object.values(this.website.tagsDefaultList).filter((tag: WebsiteTag): boolean => tag.title !== '') : null;
    this.cdnIsEnabled = this.website && !!this.website.cloudflareEnabled && !TypeUtils.isUndefined(this.userSubscription.plan.services.cloudflare_cdn);
  }

  private _addSubscriptions(): void {
    this._getCurrentWorkspace();
    this._getMigrationActivities();
    this._getSubSites();
    this.ongoingGoogleScoreActivity$ = this._store.select(fromStore.getGoogleScoreOngoingActivity(this.website.id));
  }

  private _getCurrentWorkspace(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUserSubscriptionState),
          this._store.select(fromStore.getCurrentWorkspace)
        )),
        distinctUntilChanged(([prevUser, prevWorkspace], [currUser, currWorkspace]) => prevWorkspace.workspaceId === currWorkspace.workspaceId),
        first(),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([userSubscription, currentWorkspace]) => {
        this.currentWorkspace = currentWorkspace;
      });
    /*this._store.select(fromStore.getCurrentWorkspace)
      .pipe(
        distinctUntilChanged((prev, curr) => prev && curr && prev.workspaceId === curr.workspaceId),
        filter(workspace => !!workspace),
        takeUntil(this._destroyStream$)
      )
      .subscribe(currentWorkspace => {
        this.currentWorkspace = currentWorkspace;
      });*/
  }

  private _getSubSites(): void {
    this.networkSubSitesOptions$ = this._store.select(fromStore.getWebsites)
      .pipe(
        filter(websites => !!websites && !!websites.length),
        map(websites => websites.reduce((subSites: WebsitesListItem[], website) => {
            if (website.isNetwork && website.parentId && website.parentId === this.website.id) {
              return [...subSites, {
                title: website.title,
                screenshot: website.screenshot ? 'data:image/jpg;base64,' + website.screenshot
                  : '../../../assets/images/common/no_screenshot.svg',
                redirectionUrl: `/websites/${website.id}`,
                link: website.site_url
              }];
            }
            return subSites;
          }, [])
        ),
        takeUntil(this._destroyStream$)
      );
  }

  private _getMigrationActivities(): void {
    this._store.select(fromStore.getMigrationOngoingActivity)
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((migrationActivity): void => {
        if (!migrationActivity) {
          this.disableCopyWebsite = false;
          this.migrationInProgress = false;
          return;
        }
        this.migrationInProgress = !this.website.domain_hash && migrationActivity.meta
          && parseInt(migrationActivity.meta.migrating_domain_id, 10) === this.website.id;

        this.failedMigration = migrationActivity.status === ActivityStatus.FAILED;
        switch (migrationActivity.status) {
          case ActivityStatus.DONE:
          case ActivityStatus.FAILED:
            this.disableCopyWebsite = false;
            this.loading = false;
            this.migrationInProgress = false;
            break;
          case ActivityStatus.RUNNING:
            this.loading = true;
            this.ongoingMigrationActivity = migrationActivity;
            break;
          default:
            this.disableCopyWebsite = false;
            this.websiteActions.slice(0, 1);
        }
      });
  }

  public onItemToggled(): void {
    this.toggled.emit();
  }

  public onCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.website.selected = target.checked;
    this.onItemToggled();
  }

  public showAddTagDialog(websiteId) {
    const selectedWebsitesIds = [websiteId];
    this._dialogService.addDialog(TagsListDialogComponent, {
      domainIds: selectedWebsitesIds,
      tagsList: this.allTagsList
    });
  }

  public manageWebsite(): void {
    if (this.pluginUpdating) return;
    if (this.overlay && !this.cdnIsEnabled) {
      return;
    }
    if (this.website.loggedOut) {
      this._router.navigate(['/websites/' + this.website.id + '/hosting/info']);
    } else if (this.website.connectedFrom === 'speed_optimizer') {
      this._router.navigate(['/websites/' + this.website.id + '/booster/frontend']);
    }
    else if (this.user && this.user.oneProductOrBundleClient) {
      this._router.navigate(['/websites/' + this.website.id + (this.website.isNetwork ? '/my-sites' : '/plugins')]);
    }
    else {
      this._router.navigate(['/websites/' + this.website.id + (this.website.isNetwork ? '/my-sites' : '/main')]);
    }
  }

  public migrateWebsiteDialog() {
    this._createWebsiteService.openMigrateWebsiteModal(this.website.id, this.website.site_url);
  }

  public websiteAutoLogin(): void {
    this.loading = true;
    this._websiteService.autoLogin(this.website.id)
      .pipe(
        first(response => !!response)
      )
      .subscribe(() => {
        this.loading = false;
      });
  }

  public redirectToAHA() {
    if (this.overlay) {
      return;
    }
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: "MoveTo10Web Page visit",
      eventLabel: 'Score thumb view listing click',
      websiteId: this.website && this.website.id
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);

    if (TypeUtils.isDefined(this.userSubscription.plan.services.speed_optimizer)) {
      this._router.navigateByUrl(`/websites/${this.website.id}/booster/frontend`);
    } else {
      this._router.navigateByUrl(`/websites/${this.website.id}/move-to-10web`);
    }
  }

  public toggleSubSites(hide: boolean = false): void {
    if (this.subSitesList && this.subSitesList.nativeElement && this.subSitesList.nativeElement.style
      && this.subSitesList.nativeElement.style.display === 'none' && !hide) {
      this._showSubSitesList();
    } else {
      this._hideSubSitesList();
    }
  }

  private _showSubSitesList(): void {
    this.subSitesList.nativeElement.setAttribute('style', 'display: block');
    const listRect = this.subSitesList.nativeElement.firstChild.getBoundingClientRect();
    this.subSitesList.nativeElement.firstChild.setAttribute('style',
      listRect.y + listRect.height + 60 < window.innerHeight ? 'top: 60px' : 'bottom: 60px');
  }

  private _hideSubSitesList(): void {
    this.subSitesList.nativeElement.firstChild.removeAttribute('style');
    this.subSitesList.nativeElement.setAttribute('style', 'display: none');
  }

  private _getOngoingOptimizingActivities() {
    this._store.select(fromStore.getOngoingActivities)
      .pipe(
        takeUntil(this._destroyStream$),
        map(activities => activities.find(({status, action}) => status === ActivityStatus.RUNNING && action === 'first_page_is_optimizing'))
      )
      .subscribe(activity => this.ongoingOptimizingActivities = !!activity);
  }

  private _getOngoingManagerUpdateActivities(): void {
    this._store.select(fromStore.getOngoingActivities)
      .pipe(
        takeUntil(this._destroyStream$),
        map(activities => activities.find(activity =>
          activity && activity.websiteId === this.website.id && activity.meta && activity.meta.product_data
          && activity.meta.product_data.type === "plugin" && activity.meta.product_data.slug === Config.managerPluginSlug)),
        filter(activity => !!activity),
        map(({status}) => status)
      )
      .subscribe(status => {
        this.pluginUpdating = status && status !== ActivityStatus.DONE && status !== ActivityStatus.FAILED;
      });
  }

  private _setWebsiteUrl() {
    const dbTesting: boolean = !!this._cookieService.get('db-testing');
    this.previewUrl = this.demoUser ? `${this.website.site_url}?from=websites&client_id=${this.user.id}&domain_id=${Config.demoDomainId}&first_time` + (dbTesting ? `&db-testing` : ``) : this.website.site_url;
  }
}
